import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User } from '@/types';
import { authApi, LoginCredentials } from '@/api/auth';
import { notifyInfo, notifySuccess } from '@/lib/feedback';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  hasDashboardAccess: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const normalizeRoles = useCallback((roles: unknown): string[] => {
    if (!Array.isArray(roles)) {
      return [];
    }

    return roles
      .map((role) => String(role).trim().toUpperCase())
      .filter(Boolean)
      .map((role) => (role.startsWith('ROLE_') ? role : `ROLE_${role}`));
  }, []);

  const canAccessDashboard = useCallback((roles: string[]): boolean => {
    return roles.includes('ROLE_ADMINISTRATOR') || roles.includes('ROLE_SUPER_USER');
  }, []);

  const clearAuthState = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      console.log('[AuthContext] Init auth:', { hasToken: !!token, hasUser: !!storedUser });
      
      if (token && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          parsedUser.roles = normalizeRoles(parsedUser.roles);
          if (!canAccessDashboard(parsedUser.roles)) {
            console.warn('[AuthContext] Stored user has no dashboard access, clearing session');
            clearAuthState();
            setIsLoading(false);
            return;
          }
          console.log('[AuthContext] Restored user from storage:', parsedUser.email);
          setUser(parsedUser);
        } catch (error) {
          console.error('Failed to parse stored user:', error);
          clearAuthState();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [canAccessDashboard, clearAuthState, normalizeRoles]);

  const login = useCallback(async (credentials: LoginCredentials): Promise<User> => {
    console.log('[AuthContext] Login started for:', credentials.email);
    
    const { token, data } = await authApi.login(credentials);
    console.log('[AuthContext] Login API success, token received');
    
    localStorage.setItem('token', token);
    
    let userData: User;

    if (data?.email || data?.username) {
      userData = {
        id: data.id,
        username: data.username || credentials.email,
        email: data.email || credentials.email,
        name: data.name || data.username || credentials.email,
        roles: normalizeRoles(data.roles),
        isActive: true,
      };
    } else {
      try {
        userData = await authApi.getCurrentUser(credentials.email);
        userData.roles = normalizeRoles(userData.roles);
        console.log('[AuthContext] User data fetched:', userData.email);
      } catch (userError) {
        console.warn('Failed to fetch user data after login:', userError);
        clearAuthState();
        throw new Error('Akun ini tidak dapat diverifikasi untuk akses dashboard admin.');
      }
    }

    if (!canAccessDashboard(userData.roles)) {
      clearAuthState();
      throw new Error('Akun ini tidak memiliki akses ke dashboard admin.');
    }
    
    localStorage.setItem('user', JSON.stringify(userData));
    console.log('[AuthContext] Setting user state...');
    setUser(userData);
    console.log('[AuthContext] User state set, returning');
    notifySuccess('Login berhasil', `Masuk sebagai ${userData.email}`);
    
    return userData;
  }, [canAccessDashboard, clearAuthState, normalizeRoles]);

  const logout = useCallback(async () => {
    clearAuthState();
    notifyInfo('Anda telah keluar');
  }, [clearAuthState]);

  const value = {
    user,
    isAuthenticated: !!user,
    hasDashboardAccess: !!user && canAccessDashboard(normalizeRoles(user.roles)),
    isLoading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
